<script setup>
import { isNumber } from 'lodash'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'

import { SELECT_TYPES } from '@/utils/components-configurations/app-select'
import { ELEMENT_PROGRESS_TABLE_COLUMN_COUNT_OPTIONS } from '@/utils/element-progress'
import { USER_SETTINGS_MAPPER } from '@/utils/user-settings'

import AppSelect from '@/components/ui/AppSelect/AppSelect'

defineOptions({
  name: 'ProgressViewColumnsSelect'
})

const props = defineProps({
  disabled: {
    type: Boolean
  },

  options: {
    type: Array,
    default: () => ELEMENT_PROGRESS_TABLE_COLUMN_COUNT_OPTIONS
  },

  count: {
    type: Number,
    required: true,
    validator: (value, props) => {
      return props.options.includes(value)
    }
  },

  settingKey: {
    type: String,
    default: ''
  },

  appendLabelKey: {
    type: String,
    default: 'objective.columns_settings.title'
  }
})

const { t } = useI18n()

const columnCountOptions = computed(() => {
  return props.options.map(option => {
    return {
      value: option,
      label: t(props.appendLabelKey)
    }
  })
})

const emit = defineEmits({
  'update-count': count => isNumber(count)
})

const store = useStore()

const onUpdateColumnCount = async count => {
  if (!props.options.includes(count)) return

  const isKeyValid =
    props.settingKey && Object.keys(USER_SETTINGS_MAPPER).includes(props.settingKey)

  if (isKeyValid) {
    await store.dispatch('system/updateUserSettings', {
      [USER_SETTINGS_MAPPER[props.settingKey]]: count
    })
  }
  emit('update-count', count)
}
</script>

<template>
  <AppSelect
    :data-auto-testid="$attrs['data-auto-testid'] || null"
    :data-testid="$attrs['data-testid'] || null"
    :disabled="disabled"
    :dropdown-min-width="200"
    :model-value="count"
    :offset="[0, 0]"
    :options="columnCountOptions"
    :type="SELECT_TYPES.SECONDARY_NEXT"
    class="pvc-ColumnsSelect"
    position="bottom-end"
    @update:model-value="onUpdateColumnCount"
  >
    <template #button="{ option, active }">
      <div
        v-if="option"
        :class="{
          'pvc-ColumnsSelectTrigger-active': active
        }"
        class="pvc-ColumnsSelectTrigger"
      >
        <div class="pvc-ColumnsCounter">
          {{ option.value }}
        </div>
      </div>
    </template>

    <template #option-label="{ option }">
      <div v-if="option" class="pvc-ColumnsSelect_Label">
        <div class="pvc-ColumnsCounter">
          {{ option.value }}
        </div>
        {{ option.label }}
      </div>
    </template>
  </AppSelect>
</template>

<style scoped lang="scss">
.pvc-ColumnsSelect {
  display: flex;
  --disabled-opacity: 0.5;
}

.pvc-ColumnsCounter {
  font-size: $fs-14;
  font-weight: fw('bold');
  line-height: 20px;
  color: $dark-2;
  position: relative;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 2px;
  min-width: var(--pvc-counter-size, 24px);
  height: var(--pvc-counter-size, 24px);
  box-sizing: border-box;
  padding-inline: var(--pvc-counter-padding-inline, 6px 4px);

  &:before {
    position: relative;
    display: block;
    border-radius: $border-radius-sm-next;
    content: '';
    width: 3px;
    height: 12px;
    background-color: rgba($dark-2, 0.5);
  }
}

.pvc-ColumnsSelectTrigger {
  border-radius: $border-radius-sm;
  background-color: var(--pvc-trigger-background, transparent);

  &-active {
    background-color: $grey-3-next;
  }

  @media (any-hover: hover) {
    &:hover {
      background-color: $grey-3-next;
    }
  }
}

.pvc-ColumnsSelect_Label {
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>
